import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Params } from '@angular/router';
import { PagedList, RecipeInfoModel, RecipeModel, WorkflowModel, WorkflowStepModel } from '../interface';
import { toHttpParams } from '../utils/globals';
import { UiLabel, UnitOfMeasureModel } from '../vn-api';
import { RecipeType } from '../enum';

@Injectable({
  providedIn: 'root'
})
export class HxRecipeService {

  constructor(
    private _http: HttpClient,
  ) { }

  // workflow
  getWorkflowList(params?: {
    page?: number;
    limit?: number;
    orderBy?: string;
    workflowCategoryId?: number;
    query?: string;
    brandId?: number;
    storeId?: number;
  }): Promise<PagedList<WorkflowModel>> {
    return firstValueFrom(this._http.get<PagedList<WorkflowModel>>(`/api/vn/workflows`, {params: toHttpParams(params, true)}));
  }

  getWorkflowById(workflowId: number): Promise<WorkflowModel> {
    return firstValueFrom(this._http.get<WorkflowModel>(`/api/vn/workflows/${workflowId}`));
  }

  saveWorkflow(body: WorkflowRequest): Promise<WorkflowModel> {
    return firstValueFrom(this._http.post<WorkflowModel>(`/api/vn/workflows`, body));
  }

  removeWorkflow(workflowId: number): Promise<WorkflowModel> {
    return firstValueFrom(this._http.delete<WorkflowModel>(`/api/vn/workflows/${workflowId}`));
  }

  // workflow steps
  getWorkflowStepList(workflowId: number, params?: {
    page?: number;
    limit?: number;
    query?: string;
  }): Promise<PagedList<WorkflowStepModel>> {
    return firstValueFrom(this._http.get<PagedList<WorkflowStepModel>>(`/api/vn/workflows/${workflowId}/steps`, {params: toHttpParams(params, true)}));
  }

  getWorkflowStepById(workflowId: number, workflowStepId: number): Promise<WorkflowStepModel> {
    return firstValueFrom(this._http.get<WorkflowStepModel>(`/api/vn/workflows/${workflowId}/steps/${workflowStepId}`));
  }

  saveWorkflowStep(workflowId: number, body: WorkflowStepRequest): Promise<WorkflowStepModel> {
    return firstValueFrom(this._http.post<WorkflowStepModel>(`/api/vn/workflows/${workflowId}/steps`, body));
  }

  removeWorkflowStep(workflowId: number, workflowStepId: number): Promise<WorkflowStepModel> {
    return firstValueFrom(this._http.delete<WorkflowStepModel>(`/api/vn/workflows/${workflowId}/steps/${workflowStepId}`));
  }

  // product-info recipes
  getProductRecipeList(productInfoId: number): Promise<PagedList<RecipeInfoModel>> {
    return firstValueFrom(this._http.get<PagedList<RecipeInfoModel>>(`/api/vn/product-infos/recipes?limit=50&productInfoId=${productInfoId}`));
  }

  updateProductRecipe(recipeId: number, body: any): Promise<any> {
    return firstValueFrom(this._http.put(`/api/vn/product-infos/recipes/${recipeId}`, body));
  }

  deleteProductRecipe(recipeId: number): Promise<any> {
    return firstValueFrom(this._http.delete(`/api/vn/product-infos/recipes/${recipeId}`));
  }

  // recipe
  getRecipeList(params: Params): Promise<PagedList<RecipeInfoModel>> {
    return firstValueFrom(this._http.get<PagedList<RecipeInfoModel>>(`/api/vn/recipes`, { params: params }));
  }

  getRecipeById(recipeId: number): Promise<RecipeModel> {
    return firstValueFrom(this._http.get<RecipeModel>(`/api/vn/recipes/${recipeId}`));
  }

  saveRecipe(stepId: number, body: RecipeRequest): Promise<RecipeModel> {
    return firstValueFrom(this._http.post<RecipeModel>(`/api/vn/recipes?stepId=${stepId}`, body));
  }

  deleteRecipe(recipeId: number): Promise<RecipeModel> {
    return firstValueFrom(this._http.delete<RecipeModel>(`/api/vn/recipes/${recipeId}`));
  }

  getUnit(unitId: number): Promise<UnitOfMeasureModel> {
    return firstValueFrom(this._http.get<UnitOfMeasureModel>(`/api/vn/unit-of-measures/${unitId}`));
  }
}

export interface WorkflowStepResponse {
  id: number;
  name: string;
  description: string;
  workflow: WorkflowResponse;
  order: number;
  servicePrice: number;
}

export interface WorkflowResponse {
  id: number;
  name: string;
  description: string;
  workflowCategoryId: number;
}

export interface WorkflowStepRequest {
  id: number;
  title: UiLabel;
  description: string;
  order: number;
  servicePrice: number;
}

export interface WorkflowRequest {
  id: number;
  title: UiLabel;
  description: string;
  workflowCategoryId: number;
  storeIds: number[];
}

export interface RecipeRequest {
  id: number;
  productInfoId: number;
  netAmount: number;
  grossAmount: number;
  productUnitOfMeasure: number;
  group: number;
  priority: number;
  coefficient: number;
  toUnitOfMeasure: number;
  type: RecipeType;
}
